import React, { useState } from 'react';
import { Brain, Beaker, Users, Eye, Cog, ArrowRight, ChevronRight } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { GradientTitle } from '../components/ui';
import type { CognitiveBias } from '../types';
import { Helmet } from 'react-helmet-async';

interface BiasCategory {
  icon: React.ReactNode;
  title: string;
  description: string;
  category: string;
  biases: CognitiveBias[];
}

interface LearnPageProps {
  biases: CognitiveBias[];
}

const BiasCategory: React.FC<BiasCategory> = ({ icon, title, description, category, biases }) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <Link
      to={`/category/${category.toLowerCase()}/`}
      className={`bg-white/95 backdrop-blur-sm py-4 px-3 sm:py-5 sm:px-6 rounded-3xl shadow-md border border-gray-100
        hover:shadow-lg transition-all duration-300 hover:-translate-y-0.5 group cursor-pointer w-full block`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className="flex items-start sm:items-center gap-3 sm:gap-5">
        <div className="bg-gradient-to-br from-orange-100 to-pink-100 rounded-2xl p-2.5 sm:p-4 w-12 h-12 sm:w-16 sm:h-16 
          flex items-center justify-center group-hover:scale-105 transition-transform duration-300 shrink-0
          shadow-sm">
          {icon}
        </div>
        
        <div className="flex-1 min-w-0 space-y-1.5 sm:space-y-2.5">
          <div className="flex flex-row items-center justify-between gap-2">
            <h3 className="text-base sm:text-xl font-bold text-gray-800 truncate">{title}</h3>
            <span className="text-xs sm:text-sm font-medium text-orange-500 bg-orange-50 px-2 py-0.5 sm:px-2.5 sm:py-1 rounded-full whitespace-nowrap shrink-0">{biases.length} biases</span>
          </div>
          
          <p className="text-sm sm:text-base text-gray-600 line-clamp-2 leading-relaxed">{description}</p>

          <div className="pt-0.5">
            <span className="text-orange-500 font-medium flex items-center gap-1.5 group-hover:gap-2.5 transition-all text-sm">
              Explore Category <ArrowRight className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
            </span>
          </div>
        </div>
      </div>
    </Link>
  );
};

export const LearnPage: React.FC<LearnPageProps> = ({ biases }) => {
  // Add loading state
  if (!biases || biases.length === 0) {
    return (
      <div className="min-h-screen bg-orange-50 py-12">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="flex items-center justify-center">
              <div className="text-gray-600">Loading categories...</div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  console.log('Available categories:', [...new Set(biases.map(b => b?.category))]);

  const categories: BiasCategory[] = [
    {
      icon: <Brain className="w-8 h-8 text-orange-500" />,
      title: "Memory Biases",
      category: "Memory",
      description: "Explore memory-related pitfalls and learn how they influence recall and decision-making.",
      biases: biases.filter(b => b?.category === "Memory")
    },
    {
      icon: <Beaker className="w-8 h-8 text-orange-500" />,
      title: "Decision-Making Biases",
      category: "Decision-Making",
      description: "Understand why we often make flawed decisions and discover strategies to improve judgment in complex environments.",
      biases: biases.filter(b => b?.category === "Decision-Making")
    },
    {
      icon: <Users className="w-8 h-8 text-orange-500" />,
      title: "Social Biases",
      category: "Social",
      description: "See how interpersonal dynamics and social contexts can skew our perceptions and influence our behavior.",
      biases: biases.filter(b => b?.category === "Social")
    },
    {
      icon: <Eye className="w-8 h-8 text-orange-500" />,
      title: "Perceptual Biases",
      category: "Perceptual",
      description: "Examine how our senses and attention can be tricked, leading to distorted views and misinterpretations of information.",
      biases: biases.filter(b => b?.category === "Perceptual")
    },
    {
      icon: <Cog className="w-8 h-8 text-orange-500" />,
      title: "Reasoning Biases",
      category: "Reasoning",
      description: "Dive into common logical errors and cognitive traps that impact our analytical thinking and decision-making.",
      biases: biases.filter(b => b?.category === "Reasoning")
    }
  ];

  categories.forEach(cat => {
    console.log(`${cat.title}: ${cat.biases.length} biases`);
  });

  return (
    <>
      <Helmet prioritizeSeoTags>
        <title>Learn About Cognitive Biases | Comprehensive Guide</title>
        <meta name="description" content="Explore cognitive biases and their impact on thinking. Learn how different biases affect decision-making and discover strategies to overcome them." />
        <meta name="keywords" content="cognitive bias guide, psychology learning, bias categories, critical thinking, decision-making biases" />
        <link rel="canonical" href="https://www.cognitivebiaslab.com/learn/" key="canonical" data-source="page-component" />
        
        {/* OpenGraph tags for better social sharing */}
        <meta property="og:title" content="Learn About Cognitive Biases | Comprehensive Guide" />
        <meta property="og:description" content="Explore cognitive biases and their impact on thinking. Learn how different biases affect decision-making and discover strategies to overcome them." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.cognitivebiaslab.com/learn/" />
        <meta property="og:site_name" content="Cognitive Bias Lab" />
        <meta property="og:image" content="https://www.cognitivebiaslab.com/logo.webp" />
        <meta property="og:image:width" content="512" />
        <meta property="og:image:height" content="512" />
        <meta property="og:image:type" content="image/webp" />
        
        {/* Twitter Card tags */}
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content="Learn About Cognitive Biases | Comprehensive Guide" />
        <meta name="twitter:description" content="Explore cognitive biases and their impact on thinking. Learn how different biases affect decision-making and discover strategies to overcome them." />
        <meta name="twitter:image" content="https://www.cognitivebiaslab.com/logo.webp" />
      </Helmet>

      <div className="min-h-screen bg-orange-50 py-12">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center space-y-6 mb-12">
              <GradientTitle className="text-3xl md:text-4xl mb-4" useH1={true}>
                Explore Bias Categories
              </GradientTitle>
              <div className="flex items-center justify-center">
                <p className="text-lg text-gray-600 max-w-3xl">
                  Discover different types of cognitive biases and learn how they affect your thinking and decision-making.
                  {" "}
                  <Link 
                    to="/biases/"
                    className="inline-flex items-center gap-1 text-orange-500 hover:text-orange-600 
                      transition-colors group"
                  >
                    View List of Biases
                    <ChevronRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
                  </Link>
                </p>
              </div>
            </div>

            <div className="max-w-4xl mx-auto space-y-4">
              {categories.map((category, index) => (
                <BiasCategory key={index} {...category} />
              ))}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}; 